import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  Container,
  Box,
  Typography,
  TextField,
  Alert,
  Divider,
  Link,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { GradientBackground, GlassmorphismCard, AnimatedLogo, BrandButton } from '../ui/enhanced';
import PasswordStrengthIndicator from '../ui/forms/PasswordStrengthIndicator';
import { handleValidationError, formatErrorMessage } from '../../utils/errorHandler';
import Modal from '../ui/Modal';
import TermsModalContent from '../legal/TermsModalContent';
import TransparencyModalContent from '../legal/TransparencyModalContent';

const Register: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedTransparency, setAcceptedTransparency] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showTransparencyModal, setShowTransparencyModal] = useState(false);

  const { signUp, error: authError, loading, clearError } = useAuth();
  const navigate = useNavigate();

  const handleNavigateToLogin = () => {
    clearError();
    setValidationError(null);
    navigate('/');
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      const errorState = handleValidationError('emailRequired');
      return setValidationError(formatErrorMessage(errorState));
    }

    if (password !== confirmPassword) {
      const errorState = handleValidationError('passwordsNoMatch');
      return setValidationError(formatErrorMessage(errorState));
    }

    if (password.length < 8) {
      const errorState = handleValidationError('passwordTooShort');
      return setValidationError(formatErrorMessage(errorState));
    }

    if (!acceptedTerms) {
      return setValidationError('You must accept the Terms and Conditions to continue.');
    }

    if (!acceptedTransparency) {
      return setValidationError('You must acknowledge the Transparency Notice to continue.');
    }

    try {
      setValidationError(null);
      await signUp(email, password, "portal_user");
      navigate('/dashboard');
    } catch (err: any) {
      // Error is already set in AuthContext
    }
  };

  return (
    <GradientBackground variant="brand">
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: 2,
        }}
      >
        <Container component="main" maxWidth="xs">
          <GlassmorphismCard
            intensity="medium"
            sx={{
              p: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              animation: 'fade-in 0.5s ease-in-out',
            }}
          >
            <AnimatedLogo
              src="/portal/OrangeLogo.png"
              alt="AskARC API Portal Logo"
              size={80}
              animation="float"
              sx={{ mb: 2 }}
            />
            <Typography
              component="h1"
              variant="h4"
              sx={{
                mb: 3,
                fontWeight: 700,
                background: 'linear-gradient(135deg, #753403 0%, #4F076F 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textAlign: 'center',
              }}
            >
              Create your API Portal account
            </Typography>
          
          {(validationError || authError) && (
            <Alert severity="error" sx={{ width: '100%', mb: 2, borderRadius: '8px' }}>
              {validationError || authError}
            </Alert>
          )}
          
          <Box component="form" onSubmit={handleRegister} sx={{ width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px !important',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderRadius: '8px !important',
                },
              }}
            />
            
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px !important',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderRadius: '8px !important',
                },
              }}
            />
            
            <PasswordStrengthIndicator password={password} />

            <TextField
              margin="normal"
              required
              fullWidth
              name="confirm-password"
              label="Confirm Password"
              type="password"
              id="confirm-password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={loading}
              error={confirmPassword.length > 0 && password !== confirmPassword}
              helperText={
                confirmPassword.length > 0 && password.length > 0
                  ? password === confirmPassword
                    ? '✓ Passwords match'
                    : '✗ Passwords do not match'
                  : ''
              }
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px !important',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderRadius: '8px !important',
                },
                '& .MuiFormHelperText-root': {
                  color: password === confirmPassword && confirmPassword.length > 0 ? '#4caf50' : undefined,
                },
              }}
            />

            {/* Terms and Conditions Checkbox */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  disabled={loading}
                  sx={{
                    color: '#753403',
                    '&.Mui-checked': {
                      color: '#753403',
                    },
                  }}
                />
              }
              label={
                <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                  I agree to the{' '}
                  <Link
                    component="button"
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setShowTermsModal(true);
                    }}
                    sx={{ color: '#753403', textDecoration: 'underline', cursor: 'pointer' }}
                  >
                    Terms of Use
                  </Link>
                  {' '}— I have read and accept the Terms and Conditions of AskArc.app (FenixMinds B.V., KvK 91762782).
                </Typography>
              }
              sx={{ mt: 2, alignItems: 'flex-start' }}
            />

            {/* Transparency Notice Checkbox */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={acceptedTransparency}
                  onChange={(e) => setAcceptedTransparency(e.target.checked)}
                  disabled={loading}
                  sx={{
                    color: '#753403',
                    '&.Mui-checked': {
                      color: '#753403',
                    },
                  }}
                />
              }
              label={
                <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                  I acknowledge the{' '}
                  <Link
                    component="button"
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setShowTransparencyModal(true);
                    }}
                    sx={{ color: '#753403', textDecoration: 'underline', cursor: 'pointer' }}
                  >
                    Transparency Notice
                  </Link>
                  {' '}— I understand that content may be generated or assisted by AI and that provenance is recorded as described in the Transparency Documentation.
                </Typography>
              }
              sx={{ mt: 1, mb: 1, alignItems: 'flex-start' }}
            />

            <BrandButton
              type="submit"
              fullWidth
              variant="primary"
              loading={loading}
              disabled={!email || !password || !confirmPassword || password !== confirmPassword || !acceptedTerms || !acceptedTransparency || loading}
              sx={{ mt: 3, mb: 2, py: 1.5, borderRadius: '8px' }}
            >
              Sign Up
            </BrandButton>
          </Box>
          
          <Box sx={{ mt: 2, width: '100%', textAlign: 'center' }}>
            <Divider sx={{ width: '100%', my: 2 }} />
            <Typography variant="body2" sx={{ mb: 1 }}>
              Already have an account?
            </Typography>
            <BrandButton
              variant="outlined"
              fullWidth
              onClick={handleNavigateToLogin}
              sx={{ py: 1.5, borderRadius: '8px' }}
            >
              Sign In
            </BrandButton>
          </Box>
          </GlassmorphismCard>
        </Container>
      </Box>

      {/* Terms Modal */}
      <Modal isOpen={showTermsModal} onClose={() => setShowTermsModal(false)} size="lg">
        <TermsModalContent onClose={() => setShowTermsModal(false)} />
      </Modal>

      {/* Transparency Modal */}
      <Modal isOpen={showTransparencyModal} onClose={() => setShowTransparencyModal(false)} size="lg">
        <TransparencyModalContent onClose={() => setShowTransparencyModal(false)} />
      </Modal>
    </GradientBackground>
  );
};

export default Register;
